/* eslint-disable jsx-a11y/control-has-associated-label */
import { UserPermissions } from '@centecomm/portal-service';
import { useCurrentUser } from '@centecomm/portal-service-react';
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Route } from 'react-router-dom';

import { IconColors, MenuCollapse, MenuExpand } from '../../Icons';
import { FlexWrapper } from '../../Shared/FlexWrapper';
import EmergencyAlertCreateBubble from '../EmergencyAlertCreateBubble/EmergencyAlertCreateBubble';
import { TenantPortalLogo } from '../TenantPortalLogo';
import { NavigationLink } from '../Types/NavigationLink';
import BottomNavRow from './BottomNavRow';
import styles from './navigation.module.css';
import SubMenuNav from './SubMenuNav';
import shouldShowSubMenu from './useShouldShowNestedMenus';

interface NavigationProps {
    isOpen: boolean;
    navigationLinks: NavigationLink[];
    toggleNav: () => void;
}

const noRender = (): JSX.Element => <></>;

function Navigation({ isOpen, navigationLinks, toggleNav }: NavigationProps): React.ReactElement<NavigationProps> {
    const namespaces = navigationLinks
        .flatMap(({ text, badgeText }) => [text, badgeText])
        .map((translationKey) => (translationKey?.includes(':') ? translationKey.split(':')[0] : undefined))
        .filter(Boolean);
    const { t } = useTranslation(namespaces);
    const { isOwnerOperator, hasPermission } = useCurrentUser();

    return (
        <div id="navigationPanel" className={`${styles.NavWrapper} ${isOpen ? styles.OpenNav : styles.CloseNav}`}>
            {isOpen ? (
                <>
                    <FlexWrapper align="center" justify="between" className={styles.HeaderTopRow} fullWidth>
                        <Link to="/">
                            <TenantPortalLogo />
                        </Link>
                        <button type="button" onClick={toggleNav} data-qa="toggle-navigation-collapse">
                            <MenuCollapse aspect="1.5rem" color={IconColors.MediumGrey} />
                        </button>
                    </FlexWrapper>
                    <nav className={styles.Navigation}>
                        <ul>
                            {navigationLinks.map(({ Icon, text, url, badgeText, childItems }: NavigationLink) => (
                                <li
                                    key={`core-navigation-${url.replace('/', '').toLowerCase()}`}
                                    data-qa={`core-navigation-${text}`}
                                >
                                    <NavLink
                                        data-qa-navigation-link={text}
                                        className="dropdown-item"
                                        to={url || ''}
                                        activeClassName={styles.ActiveItem}
                                    >
                                        <FlexWrapper align="center" justify="start" gap="0.5rem">
                                            <Route strict path={url}>
                                                {({ match }) => (
                                                    <Icon
                                                        aspect="1.25rem"
                                                        color={match ? IconColors.LightGrey : IconColors.Grey}
                                                    />
                                                )}
                                            </Route>
                                            <span>{t(text)}</span>
                                            {badgeText && <span className={styles.NavBadge}>{t(badgeText)}</span>}
                                        </FlexWrapper>
                                    </NavLink>
                                    {childItems?.length && shouldShowSubMenu(childItems, url) ? (
                                        <SubMenuNav features={childItems} />
                                    ) : (
                                        noRender()
                                    )}
                                </li>
                            ))}
                        </ul>
                    </nav>
                    {!isOwnerOperator && hasPermission(UserPermissions.CreateAlert) && <EmergencyAlertCreateBubble />}
                </>
            ) : (
                <>
                    <FlexWrapper align="center" justify="between" className={styles.HeaderTopRow} fullWidth>
                        <button type="button" onClick={toggleNav} data-qa="toggle-navigation-collapse">
                            <MenuExpand aspect="1.5rem" color={IconColors.MediumGrey} />
                        </button>
                    </FlexWrapper>
                    <nav className={classnames(styles.Navigation, styles.CollapsedNavigation)}>
                        <ul>
                            {navigationLinks.map(({ Icon, text, url }: NavigationLink) => (
                                <li
                                    key={`core-navigation-${url.replace('/', '').toLowerCase()}`}
                                    data-qa={`core-navigation-${text}`}
                                >
                                    <NavLink
                                        data-qa-navigation-link={text}
                                        className="dropdown-item"
                                        to={url || ''}
                                        activeClassName={styles.ActiveItem}
                                    >
                                        <FlexWrapper align="center" justify="start" gap="0.5rem">
                                            <Route strict path={url}>
                                                {({ match }) => (
                                                    <Icon
                                                        aspect="1.25rem"
                                                        color={match ? IconColors.LightGrey : IconColors.Grey}
                                                    />
                                                )}
                                            </Route>
                                        </FlexWrapper>
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </>
            )}
            <BottomNavRow navIsOpen={isOpen} />
        </div>
    );
}

export default Navigation;
